import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Sale } from '../../types/sales';
import { deleteSale } from '../../features/sales/salesSlice';
import EditSaleForm from './EditSaleForm';
import Card from '../ui/Card';
import Button from '../ui/Button';
import { formatCurrency, formatDate } from '../../utils/formatters';

interface Props {
  sale: Sale;
}

const SaleCard: React.FC<Props> = ({ sale }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);

  const handleDelete = async () => {
    if (window.confirm(t('common.areYouSure'))) {
      await dispatch(deleteSale(sale.id));
    }
  };

  if (isEditing) {
    return (
      <EditSaleForm
        sale={sale}
        onCancel={() => setIsEditing(false)}
        onSuccess={() => setIsEditing(false)}
      />
    );
  }

  return (
    <Card>
      <div className="space-y-4">
        <div>
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {sale.crops?.name || t('sales.unknownCrop')}
                {sale.crops?.variety && ` (${sale.crops.variety})`}
              </h3>
              {sale.vendors && (
                <p className="text-sm text-gray-600">{t('sales.vendor')}: {sale.vendors.name}</p>
              )}
            </div>
            <div className="text-right">
              <p className="text-lg font-semibold text-gray-900">
                {formatCurrency(sale.total_amount)}
              </p>
              {sale.profit && (
                <p className={`text-sm ${sale.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {t('sales.profit')}: {formatCurrency(sale.profit)}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="text-sm text-gray-600">
          <p>{t('common.quantity')}: {sale.quantity} {sale.unit}</p>
          <p>{t('sales.unitPrice')}: {formatCurrency(sale.unit_price)}</p>
          {sale.purchase_price > 0 && (
            <p>{t('sales.purchasePrice')}: {formatCurrency(sale.purchase_price)}</p>
          )}
          <p>{t('sales.saleDate')}: {formatDate(sale.sale_date)}</p>
          {sale.notes && (
            <div className="mt-3 p-2 bg-gray-50 rounded-md">
              <p className="text-sm text-gray-600 italic">{sale.notes}</p>
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setIsEditing(true)}
          >
            {t('common.edit')}
          </Button>
          <Button
            size="sm"
            variant="danger"
            onClick={handleDelete}
          >
            {t('common.delete')}
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default SaleCard;